// DOM overlays — watermark, cycle counter, ticker, sound chip, info panel,
// move hint. All overlays use OKLCH-derived colors via color-mix() so they
// blend with today's palette without being garish.
//
// Pure DOM, no React. The page is a single canvas with a thin layer of typography
// on top — that's the design. Each overlay function takes the parameters and
// returns no value; it mutates the DOM idempotently. Re-call on parameter change.

import type { Parameters } from '../lib/parameters-schema';
import { paletteToCssVars } from './palette';

export interface OverlayHandles {
  setParameters(params: Parameters): void;
  showMoveHint(): void;
  hideMoveHint(): void;
  setSoundState(state: 'off' | 'live' | 'unsupported'): void;
  toggleInfoPanel(): void;
  isInfoPanelOpen(): boolean;
}

export function mountOverlays(root: HTMLElement, initial: Parameters): OverlayHandles {
  // Style block — one-shot inject, idempotent
  ensureStyles();

  const layer = document.createElement('div');
  layer.id = 'herminator-overlays';
  layer.setAttribute('aria-live', 'polite');
  root.appendChild(layer);

  const watermark = makeWatermark();
  const cycle = makeCycle(initial);
  const ticker = makeTicker(initial);
  const moveHint = makeMoveHint();
  const soundChip = makeSoundChip();
  const infoToggle = makeInfoToggle();
  const infoPanel = makeInfoPanel(initial);

  layer.append(watermark, cycle, ticker, moveHint, soundChip, infoToggle, infoPanel);

  paletteToCssVars(initial.palette, root);

  let infoOpen = false;
  const setInfoOpen = (next: boolean): void => {
    infoOpen = next;
    infoPanel.classList.toggle('herm-info--open', next);
    infoToggle.setAttribute('aria-expanded', String(next));
  };
  infoToggle.addEventListener('click', () => setInfoOpen(!infoOpen));
  infoPanel.addEventListener('click', (e) => {
    if (e.target === infoPanel) setInfoOpen(false);
  });
  document.addEventListener('keydown', (e) => {
    if ((e.key === 'h' || e.key === 'H') && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      setInfoOpen(!infoOpen);
    }
    if (e.key === 'Escape' && infoOpen) setInfoOpen(false);
  });

  return {
    setParameters(p): void {
      paletteToCssVars(p.palette, root);
      cycle.textContent = formatCycle(p);
      renderTicker(ticker, p);
      renderInfoPanel(infoPanel, p);
    },
    showMoveHint(): void {
      moveHint.classList.add('herm-hint--visible');
    },
    hideMoveHint(): void {
      moveHint.classList.remove('herm-hint--visible');
    },
    setSoundState(state): void {
      soundChip.dataset.state = state;
      soundChip.textContent = state === 'live'
        ? '[sound: live]'
        : state === 'unsupported'
          ? '[sound: n/a]'
          : '[sound: off]';
      soundChip.setAttribute('aria-label', soundChip.textContent ?? '');
    },
    toggleInfoPanel(): void {
      setInfoOpen(!infoOpen);
    },
    isInfoPanelOpen(): boolean {
      return infoOpen;
    },
  };
}

function makeWatermark(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'herm-watermark';
  el.textContent = '[hrmntr://]';
  return el;
}

function makeCycle(p: Parameters): HTMLElement {
  const el = document.createElement('div');
  el.className = 'herm-cycle';
  el.textContent = formatCycle(p);
  return el;
}

function formatCycle(p: Parameters): string {
  const date = (p.generated_at && p.generated_at.slice(0, 10)) || '----';
  return `cycle ${String(p.cycle).padStart(3, '0')} · ${date}`;
}

function makeTicker(p: Parameters): HTMLElement {
  const el = document.createElement('div');
  el.className = 'herm-ticker';
  renderTicker(el, p);
  return el;
}

function renderTicker(el: HTMLElement, p: Parameters): void {
  const lines = (p.signal_ticker.length ? p.signal_ticker : ['signal: silent']).slice(0, 6);
  // Duplicate the marquee content so the seamless scroll wraps cleanly
  const seq = [...lines, ...lines].map((line) => `<span class="herm-ticker-item">${escapeHtml(line)}</span>`).join('<span class="herm-ticker-sep">▪</span>');
  el.innerHTML = `<div class="herm-ticker-track">${seq}</div>`;
}

function makeMoveHint(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'herm-hint';
  // Coarse pointer (touch-primary devices) never moves a hovering cursor —
  // the hint that teaches "feed the field" should match the verb the user
  // actually has. matchMedia is the canonical detection surface; falls back
  // to "move." when matchMedia is unavailable (vintage browsers, SSR).
  const isCoarse = typeof matchMedia === 'function'
    && matchMedia('(pointer: coarse)').matches;
  el.textContent = isCoarse ? 'swipe.' : 'move.';
  return el;
}

function makeSoundChip(): HTMLButtonElement {
  const el = document.createElement('button');
  el.className = 'herm-chip herm-chip--sound';
  el.type = 'button';
  el.dataset.state = 'off';
  el.textContent = '[sound: off]';
  el.setAttribute('aria-label', '[sound: off]');
  return el;
}

function makeInfoToggle(): HTMLButtonElement {
  const el = document.createElement('button');
  el.className = 'herm-chip herm-chip--info';
  el.type = 'button';
  el.textContent = '[H]';
  el.setAttribute('aria-label', 'Toggle info panel');
  el.setAttribute('aria-expanded', 'false');
  return el;
}

function makeInfoPanel(p: Parameters): HTMLDialogElement {
  const el = document.createElement('dialog') as HTMLDialogElement;
  el.className = 'herm-info';
  renderInfoPanel(el, p);
  return el;
}

function renderInfoPanel(el: HTMLElement, p: Parameters): void {
  const sources = p.sources.length
    ? p.sources.map((s) => `<li><a href="${escapeAttr(s.url)}" rel="noreferrer noopener" target="_blank">${escapeHtml(s.title || s.url)}</a></li>`).join('')
    : '<li class="herm-info-empty">(no sources this cycle)</li>';

  el.innerHTML = `
    <div class="herm-info-card">
      <header>
        <h2>[hrmntr://]</h2>
        <button class="herm-info-close" type="button" aria-label="Close">×</button>
      </header>
      <p class="herm-info-blurb">A daily-rendered hallucination of the world's mood, possessed by Oslo. Visit again tomorrow.</p>
      <dl class="herm-info-grid">
        <dt>cycle</dt><dd>${escapeHtml(String(p.cycle))}</dd>
        <dt>generated</dt><dd>${escapeHtml(p.generated_at)}</dd>
        <dt>hue</dt><dd>${escapeHtml(p.hue_shift.toFixed(0))}°</dd>
        <dt>BPM</dt><dd>${escapeHtml(String(p.beat_bpm))}</dd>
        <dt>agitation</dt><dd>${escapeHtml(p.agitation.toFixed(2))}</dd>
        <dt>mosh</dt><dd>${escapeHtml(p.mosh_intensity.toFixed(2))}</dd>
        <dt>scanlines</dt><dd>${escapeHtml(p.scanline_density.toFixed(2))}</dd>
        <dt>palette</dt><dd>${p.palette.map((c) => `<code style="background:${escapeAttr(c)}40;border:1px solid ${escapeAttr(c)};">${escapeHtml(c)}</code>`).join(' ')}</dd>
      </dl>
      <h3>sources today</h3>
      <ul class="herm-info-sources">${sources}</ul>
      <h3>controls</h3>
      <ul class="herm-info-controls">
        <li><kbd>H</kbd> toggle this panel</li>
        <li><kbd>S</kbd> or long-press · freeze + share</li>
        <li><kbd>↑↑↓↓←→←→BA</kbd> arcade</li>
        <li><kbd>touch</kbd> ↑↑↓↓←→←→•• arcade</li>
        <li>move pointer · feed the field</li>
      </ul>
      <footer>herman@herminator.no · <a href="https://github.com/hrminator" rel="noreferrer noopener" target="_blank">github</a></footer>
    </div>
  `;
  const close = el.querySelector('.herm-info-close');
  close?.addEventListener('click', (e) => {
    e.stopPropagation();
    el.classList.remove('herm-info--open');
    document.querySelector('.herm-chip--info')?.setAttribute('aria-expanded', 'false');
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}
function escapeAttr(s: string): string {
  return escapeHtml(s);
}

let stylesInjected = false;
function ensureStyles(): void {
  if (stylesInjected || typeof document === 'undefined') return;
  const css = `
/* Page-wide touch gesture defaults — kill pull-to-refresh and horizontal-swipe
   navigation so swipe-Konami sequences aren't consumed by the browser before
   reaching the page. Per-surface overrides below preserve scroll where needed
   (info panel pan-y) and the canvas's own touch-action: none in index.html. */
html, body { overscroll-behavior: none; }

:root {
  --herm-pal-a: oklch(70% 0.18 320);
  --herm-pal-b: oklch(75% 0.18 200);
  --herm-pal-c: oklch(20% 0.04 290);
  --herm-fg: color-mix(in oklch, var(--herm-pal-a) 30%, white 70%);
  --herm-fg-dim: color-mix(in oklch, var(--herm-pal-a) 25%, white 50%);
  --herm-stroke: color-mix(in oklch, var(--herm-pal-a) 50%, transparent 50%);
}
#herminator-overlays {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 10;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  -webkit-font-smoothing: antialiased;
}
#herminator-overlays > * { pointer-events: auto; }
#herminator-overlays .herm-watermark,
#herminator-overlays .herm-cycle,
#herminator-overlays .herm-ticker { pointer-events: none; }

.herm-watermark {
  position: absolute;
  top: clamp(12px, 2vw, 20px);
  left: clamp(12px, 2vw, 20px);
  font-size: clamp(0.85rem, 1.2vw, 1.05rem);
  letter-spacing: 0.18em;
  opacity: 0.95;
  color: color-mix(in oklch, var(--herm-pal-a) 25%, white 75%);
  text-shadow:
    0 0 12px color-mix(in oklch, var(--herm-pal-a) 60%, transparent),
    0 0 1px rgba(0,0,0,0.6);
}
.herm-cycle {
  position: absolute;
  top: clamp(12px, 2vw, 20px);
  right: clamp(12px, 2vw, 20px);
  font-size: clamp(0.7rem, 1vw, 0.85rem);
  letter-spacing: 0.16em;
  opacity: 0.85;
  color: color-mix(in oklch, var(--herm-pal-b) 25%, white 75%);
  text-shadow:
    0 0 8px color-mix(in oklch, var(--herm-pal-b) 50%, transparent),
    0 0 1px rgba(0,0,0,0.5);
  font-variant-numeric: tabular-nums;
}
.herm-ticker {
  position: absolute;
  left: 0;
  right: 0;
  bottom: clamp(14px, 2.4vw, 28px);
  font-size: clamp(0.7rem, 1vw, 0.85rem);
  letter-spacing: 0.1em;
  overflow: hidden;
  white-space: nowrap;
  color: color-mix(in oklch, var(--herm-pal-b) 20%, white 80%);
  text-shadow: 0 0 6px color-mix(in oklch, var(--herm-pal-b) 40%, transparent), 0 0 1px rgba(0,0,0,0.7);
  -webkit-mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
          mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
}
.herm-ticker-track {
  display: inline-block;
  padding-left: 100%;
  animation: herm-ticker-roll 60s linear infinite;
}
.herm-ticker-item, .herm-ticker-sep {
  display: inline-block;
  margin: 0 0.7em;
  opacity: 0.85;
}
.herm-ticker-sep { opacity: 0.4; }
@keyframes herm-ticker-roll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
.herm-hint {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: clamp(1rem, 1.6vw, 1.4rem);
  letter-spacing: 0.4em;
  opacity: 0;
  transition: opacity 1.6s ease;
  mix-blend-mode: difference;
  pointer-events: none;
  text-transform: lowercase;
}
.herm-hint--visible { opacity: 0.55; }
.herm-chip {
  position: absolute;
  bottom: clamp(48px, 5.5vw, 64px);
  font-family: inherit;
  font-size: clamp(0.7rem, 0.95vw, 0.85rem);
  letter-spacing: 0.16em;
  /* Kill the 300ms double-tap zoom delay on iOS Safari for tap targets. */
  touch-action: manipulation;
  /* Chip text always uses an OKLCH-mixed light tone, not raw palette[A] —
     guarantees legibility against any backdrop. */
  color: color-mix(in oklch, var(--herm-pal-a) 25%, white 75%);
  /* Backdrop is a dark wash so chips stay readable over bright field areas. */
  background: linear-gradient(180deg,
    color-mix(in oklch, var(--herm-pal-c) 10%, #0a0010 90%) 0%,
    color-mix(in oklch, var(--herm-pal-c) 20%, #15001f 100%) 100%);
  border: 1px solid color-mix(in oklch, var(--herm-pal-a) 60%, transparent 40%);
  border-radius: 999px;
  padding: 0.45em 1em;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 16px color-mix(in oklch, var(--herm-pal-a) 25%, transparent);
}
.herm-chip:hover {
  background: color-mix(in oklch, var(--herm-pal-a) 25%, var(--herm-pal-c) 75%);
  transform: translateY(-1px);
}
.herm-chip:focus-visible {
  outline: 2px solid var(--herm-pal-b);
  outline-offset: 2px;
}
.herm-chip--sound { right: clamp(12px, 2vw, 20px); }
.herm-chip--info { left: clamp(12px, 2vw, 20px); }
.herm-chip--sound[data-state="live"] {
  border-color: color-mix(in oklch, var(--herm-pal-b) 80%, transparent);
  color: color-mix(in oklch, var(--herm-pal-b) 30%, white 70%);
}

.herm-info {
  position: fixed;
  inset: 0;
  margin: 0;
  padding: 0;
  border: none;
  /* Preserve vertical scroll inside the panel; contain overscroll so the
     panel doesn't transfer scroll momentum back to body. */
  touch-action: pan-y;
  overscroll-behavior: contain;
  /* Aggressive darkening — palette[2] can be any color; we want panel
     readability regardless. 12% palette tint over near-black gives that. */
  background:
    linear-gradient(135deg,
      color-mix(in oklch, var(--herm-pal-c) 12%, #07050d 88%),
      color-mix(in oklch, var(--herm-pal-a) 8%, #050308 92%));
  color: color-mix(in oklch, var(--herm-pal-a) 20%, white 80%);
  display: none;
  pointer-events: auto;
  z-index: 50;
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  overflow: auto;
}
.herm-info--open { display: block; }
.herm-info-card {
  max-width: 720px;
  margin: clamp(1rem, 6vh, 4rem) auto;
  padding: clamp(1rem, 3vw, 2rem);
  font-family: inherit;
  line-height: 1.55;
}
.herm-info-card header {
  display: flex; justify-content: space-between; align-items: baseline;
  margin-bottom: 0.6rem;
}
.herm-info-card h2 {
  margin: 0; font-size: clamp(1rem, 1.6vw, 1.3rem);
  letter-spacing: 0.2em; font-weight: 600;
}
.herm-info-close {
  background: transparent; border: 1px solid var(--herm-stroke);
  color: inherit; font-size: 1.4rem; line-height: 1; padding: 0 0.5em;
  border-radius: 4px; cursor: pointer;
}
.herm-info-blurb { opacity: 0.8; max-width: 60ch; }
.herm-info-grid {
  display: grid; grid-template-columns: 6em 1fr; gap: 0.4rem 1.2rem;
  margin: 1rem 0; font-size: 0.85rem;
}
.herm-info-grid dt { opacity: 0.55; }
.herm-info-grid dd { margin: 0; }
.herm-info-grid code {
  font-family: inherit; padding: 0.1em 0.5em;
  border-radius: 3px; font-size: 0.8em;
}
.herm-info h3 {
  margin: 1.4rem 0 0.4rem; font-size: 0.85rem;
  letter-spacing: 0.16em; opacity: 0.6; text-transform: lowercase; font-weight: 500;
}
.herm-info-sources, .herm-info-controls {
  list-style: none; padding: 0; margin: 0; font-size: 0.85rem;
}
.herm-info-sources li, .herm-info-controls li {
  padding: 0.3rem 0; border-top: 1px solid color-mix(in oklch, var(--herm-stroke) 50%, transparent);
}
.herm-info-sources li:first-child, .herm-info-controls li:first-child { border-top: 0; }
.herm-info-sources a { color: color-mix(in oklch, var(--herm-pal-b) 30%, white 70%); }
.herm-info kbd {
  font-family: inherit; font-size: 0.78em;
  padding: 0.1em 0.45em; border: 1px solid var(--herm-stroke);
  border-radius: 3px; margin-right: 0.4em;
  background: color-mix(in oklch, var(--herm-pal-c) 50%, transparent);
}
.herm-info footer {
  margin-top: 2rem; padding-top: 1rem; border-top: 1px solid var(--herm-stroke);
  font-size: 0.8rem; opacity: 0.7;
}
.herm-info-empty { opacity: 0.5; }

@media (max-width: 480px) {
  .herm-cycle { display: none; }
  .herm-chip { font-size: 0.7rem; }
}

@media (prefers-reduced-motion: reduce) {
  .herm-ticker-track { animation-duration: 240s; }
  .herm-hint { transition-duration: 200ms; }
}

/* Chrome yields entirely while arcade is mounted. Native :has() parent
   selector — single declarative rule, no JS coupling, cleanup automatic
   when .herm-arcade is removed. Baseline 2023 / Safari 15.4+.
   Also catches .herm-holdstill (the 5s-idle [HRMNTR] pulse from
   hold-still.ts, mounted directly on body — outside #herminator-overlays). */
body:has(.herm-arcade) #herminator-overlays,
body:has(.herm-arcade) .herm-holdstill { display: none; }
`;
  const style = document.createElement('style');
  style.id = 'herminator-overlay-styles';
  style.textContent = css;
  document.head.appendChild(style);
  stylesInjected = true;
}
